import { useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { reports } from '@/data';
import { Badge, type Tone } from '@/components/ui/Badge';
import { SectionCard } from '@/components/ui/Card';
import { FieldGrid } from '@/components/ui/FieldGrid';
import { Btn } from '@/components/ui/Button';
import { PageHeader } from '@/components/layout/PageHeader';

export default function Report() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const RP = reports;
  const ln = (ko: string, mn: string) => (lang === 'ko' ? ko : mn);
  const [selNo, setSelNo] = useState(RP[0].no);
  const r = RP.find((x) => x.no === selNo)!;

  const statusTone: Record<string, Tone> = { rpApproved: 'success', rpReview: 'warning', rpDraft: 'neutral' };
  const sevTone: Record<string, Tone> = { high: 'danger', mid: 'warning', info: 'info' };
  const sevLabel: Record<string, string> = { high: t('sevHigh'), mid: t('sevMid'), info: t('sevInfo') };
  const maxC = Math.max(...r.contacts.map((c) => c.count));

  return (
    <div className="mp-page" data-screen-label="분석 보고서">
      <PageHeader
        crumbs={[t('home'), t('mForensic'), t('mReport')]}
        title={t('rpTitle')}
        titleExtra={<div className="mp-pagehead__meta"><span className="mp-pagehead__updated">{t('rpSub')}</span></div>}
        actions={
          <Fragment>
            <Btn icon="bi-printer">{t('print')}</Btn>
            <Btn kind="primary" icon="bi-file-earmark-pdf">{t('downloadPdf')}</Btn>
          </Fragment>
        }
      />

      <div className="mp-grid mp-grid--master-detail">
        {/* 보고서 목록 */}
        <SectionCard flush>
          <ul className="mp-sublist">
            {RP.map((rep) => (
              <li key={rep.no}>
                <button type="button"
                  className={'mp-sublist__item' + (rep.no === selNo ? ' is-current' : '')}
                  onClick={() => setSelNo(rep.no)}>
                  <span className="mp-sublist__name" style={{ fontSize: 12.5, lineHeight: 1.35 }}>{ln(rep.titleKo, rep.titleMn)}</span>
                  <span className="mp-sublist__en">{rep.no} · {rep.date}</span>
                  <span style={{ marginTop: 4 }}><Badge tone={statusTone[rep.status]}>{t(rep.status)}</Badge></span>
                </button>
              </li>
            ))}
          </ul>
        </SectionCard>

        {/* 보고서 본문 */}
        <div className="mp-report">
          <div className="mp-report__head">
            <div className="mp-report__titlerow">
              <h3>{ln(r.titleKo, r.titleMn)}</h3>
              <Badge tone={statusTone[r.status]}>{t(r.status)}</Badge>
            </div>
            <FieldGrid rows={[
              [t('rpNo'), <span className="mp-caseno">{r.no}</span>],
              [t('relatedCase'), r.caseNo],
              [t('rpAuthor'), r.author],
              [t('rpDate'), r.date],
              [t('rpSecurity'), <Badge tone="warning">{r.security}</Badge>],
              [t('rpTool'), r.tool],
            ]} />
          </div>

          <div className="mp-report__sec">
            <h4 className="mp-report__h"><span className="mp-report__hnum">1</span>{t('rpOverview')}</h4>
            <p className="mp-overview">{ln(r.overviewKo, r.overviewMn)}</p>
          </div>

          <div className="mp-report__sec">
            <h4 className="mp-report__h"><span className="mp-report__hnum">2</span>{t('rpExtracted')}</h4>
            <div className="mp-artgrid mp-artgrid--4">
              {r.artifacts.map((a, i) => (
                <div key={i} className="mp-art">
                  <i className={'bi ' + a.icon}></i>
                  <span className="mp-art__num">{a.count}</span>
                  <span className="mp-art__lbl">{ln(a.labelKo, a.labelMn)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mp-report__sec">
            <h4 className="mp-report__h"><span className="mp-report__hnum">3</span>{t('rpFindings')}</h4>
            <ul className="mp-findings">
              {r.findings.map((f, i) => (
                <li key={i}>
                  <Badge tone={sevTone[f.sev]}>{sevLabel[f.sev]}</Badge>
                  <span>{ln(f.ko, f.mn)}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mp-report__sec">
            <h4 className="mp-report__h"><span className="mp-report__hnum">4</span>{r.chartTitleKo && r.chartTitleMn ? ln(r.chartTitleKo, r.chartTitleMn) : t('rpCommFreq')}</h4>
            <ul className="mp-barlist">
              {r.contacts.map((c, i) => (
                <li key={i}>
                  <span className="mp-barlist__label">{c.name}</span>
                  <span className="mp-barlist__track">
                    <span className="mp-barlist__fill" style={{ width: (c.count / maxC * 100) + '%', background: i === 0 ? '#0054A6' : '#5B8BC4' }}></span>
                  </span>
                  <span className="mp-barlist__val">{c.count}<small>{r.chartUnitKo && r.chartUnitMn ? ln(r.chartUnitKo, r.chartUnitMn) : t('rpTimes')}</small></span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mp-report__sec mp-report__sec--concl">
            <h4 className="mp-report__h"><span className="mp-report__hnum">5</span>{t('rpConclusion')}</h4>
            <p className="mp-overview">{ln(r.conclusionKo, r.conclusionMn)}</p>
            <div className="mp-report__sign">
              <span>{t('rpAuthor')}: <strong>{r.author}</strong></span>
              <span>{r.date}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
